import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ErrorState, FullPageLoader } from '@/components/states';
import { CurrentUserProvider, useCurrentUser } from '@/lib/current-user';
import { useSession } from '@/lib/auth';
import { useMeQuery } from '@/hooks/use-me';
import { SignInPage } from '@/pages/sign-in-page';
import { ApiError } from '@/lib/api-client';

/**
 * Resolves the viewer before the app renders.
 *
 * What an anonymous visitor gets is an admin setting, decided by the API:
 *   - a public catalogue: `/me` answers on the unauthenticated mirror with `role: 'anonymous'`,
 *     the visitor browses with sharer identity withheld, and only admin actions need an identity;
 *   - a private catalogue: the mirror answers 401 to everything, `/me` included, and the visitor
 *     gets the sign-in page — nothing of the catalogue is ever fetched.
 * Either way the API enforces it; this component only follows what `/me` said.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const session = useSession();
  const me = useMeQuery();

  if (session.status === 'loading') {
    return <FullPageLoader label="Checking your session…" />;
  }

  if (me.isPending) {
    return <FullPageLoader label="Loading your profile…" />;
  }

  if (me.isError) {
    // A 401 here means the catalogue is private and there is no session, or the id token was
    // rejected (the api client has already cleared it). Either way: the sign-in screen, not an
    // error.
    if (me.error instanceof ApiError && me.error.isUnauthorized) {
      return <SignInPage />;
    }
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-lg space-y-4">
          <ErrorState
            title="Could not load your profile"
            error={me.error}
            onRetry={() => void me.refetch()}
          />
          <div className="flex justify-center">
            <Button type="button" variant="ghost" onClick={session.signOut}>
              Sign out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <CurrentUserProvider user={me.data}>{children}</CurrentUserProvider>;
}

/**
 * Admin-only route guard. Purely a UX affordance — the API independently authorises every
 * admin endpoint, so a viewer who forces the URL still cannot do anything.
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const user = useCurrentUser();
  if (user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * Signed-in-only route guard: viewer or admin, never anonymous. Used by pages that expose people
 * and channel structure — the statistics page — which the public catalogue deliberately hides.
 * As with RequireAdmin this is only a UX affordance; the API returns 401 to anonymous callers
 * regardless of what renders here.
 */
export function RequireSignedIn({ children }: { children: ReactNode }) {
  const user = useCurrentUser();
  if (user.role === 'anonymous') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
